// ---------------------------------------------------------------------------
// DSS-23: Per-breakpoint style overrides — desktop/tablet/mobile cascade
// ---------------------------------------------------------------------------
import { useCallback, useId } from "react";
import { Monitor, Tablet, Smartphone } from "lucide-react";
import type { ReactNode } from "react";

// ── Types ──
export type Breakpoint = "desktop" | "tablet" | "mobile";

export interface BreakpointOverride {
  readonly [property: string]: string | undefined;
}

export interface ResponsiveConfig {
  readonly desktop: BreakpointOverride;
  readonly tablet: BreakpointOverride;
  readonly mobile: BreakpointOverride;
}

// ── Constants ──
export const BREAKPOINTS = {
  desktop: { label: "Desktop", minWidth: 1025, maxWidth: null },
  tablet: { label: "Tablet", minWidth: 768, maxWidth: 1024 },
  mobile: { label: "Mobile", minWidth: null, maxWidth: 767 },
} as const;

const BREAKPOINT_ORDER: readonly Breakpoint[] = ["desktop", "tablet", "mobile"];

const BREAKPOINT_ICONS: Record<Breakpoint, ReactNode> = {
  desktop: <Monitor className="h-3.5 w-3.5" />,
  tablet: <Tablet className="h-3.5 w-3.5" />,
  mobile: <Smartphone className="h-3.5 w-3.5" />,
};

const COMMON_PROPERTIES: readonly string[] = [
  "font-size",
  "line-height",
  "padding",
  "margin",
  "width",
  "height",
  "gap",
  "flex-direction",
  "text-align",
  "display",
];

const DEFAULT_CONFIG: ResponsiveConfig = {
  desktop: {},
  tablet: {},
  mobile: {},
};

// ── Props ──
interface ResponsiveOverridesProps {
  readonly value: ResponsiveConfig;
  readonly onChange: (config: ResponsiveConfig) => void;
}

// ── Component ──
export default function ResponsiveOverrides({ value, onChange }: ResponsiveOverridesProps) {
  const uid = useId();
  const config = value ?? DEFAULT_CONFIG;

  const activeBreakpoint = useActiveBreakpoint();

  const handlePropertyChange = useCallback(
    (breakpoint: Breakpoint, property: string, cssValue: string | undefined) => {
      const bp = { ...config[breakpoint] };
      if (cssValue === undefined || cssValue === "") {
        delete bp[property];
      } else {
        bp[property] = cssValue;
      }
      onChange({ ...config, [breakpoint]: bp });
    },
    [config, onChange],
  );

  const handleAddProperty = useCallback(
    (breakpoint: Breakpoint, property: string) => {
      const bp = { ...config[breakpoint] };
      bp[property] = "";
      onChange({ ...config, [breakpoint]: bp });
    },
    [config, onChange],
  );

  const handleRemoveProperty = useCallback(
    (breakpoint: Breakpoint, property: string) => {
      const bp = { ...config[breakpoint] };
      delete bp[property];
      onChange({ ...config, [breakpoint]: bp });
    },
    [config, onChange],
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
        <Monitor className="h-3.5 w-3.5" />
        Responsive Overrides
      </div>

      {/* Breakpoint tabs */}
      {BREAKPOINT_ORDER.map((bp) => {
        const isActive = activeBreakpoint === bp;
        const overrideCount = Object.keys(config[bp]).length;

        return (
          <div key={bp} className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <div
                className={[
                  "flex items-center gap-1.5 rounded-md border px-2 py-1",
                  isActive
                    ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10"
                    : "border-[var(--color-border)]",
                ].join(" ")}
              >
                {BREAKPOINT_ICONS[bp]}
                <span className="text-[10px] font-semibold text-[var(--color-foreground)]">
                  {BREAKPOINTS[bp].label}
                </span>
                {overrideCount > 0 && (
                  <span className="rounded-full bg-[var(--color-primary)] px-1.5 py-0.5 text-[8px] font-bold text-white">
                    {overrideCount}
                  </span>
                )}
              </div>
              <BreakpointRange breakpoint={bp} />
            </div>

            {/* Property list */}
            <div className="flex flex-col gap-1 pl-4">
              {Object.entries(config[bp]).map(([prop, val]) => (
                <PropertyRow
                  key={prop}
                  uid={uid}
                  breakpoint={bp}
                  property={prop}
                  value={val ?? ""}
                  cascadedValue={getCascadedValue(config, bp, prop)}
                  onChange={(v) => handlePropertyChange(bp, prop, v)}
                  onRemove={() => handleRemoveProperty(bp, prop)}
                />
              ))}

              {/* Add property */}
              <AddPropertyButton
                breakpoint={bp}
                existingProperties={Object.keys(config[bp])}
                onAdd={(prop) => handleAddProperty(bp, prop)}
              />
            </div>
          </div>
        );
      })}

      {/* CSS preview */}
      <div className="rounded-md bg-[var(--color-muted)] px-3 py-2">
        <code className="whitespace-pre-wrap text-[10px] text-[var(--color-muted-foreground)]">
          {generateResponsiveCss(config, ".selector")}
        </code>
      </div>
    </div>
  );
}

// ── Sub-components ──

function BreakpointRange({ breakpoint }: { readonly breakpoint: Breakpoint }) {
  const bp = BREAKPOINTS[breakpoint];
  let range = "";
  if (bp.minWidth !== null && bp.maxWidth !== null) {
    range = `${bp.minWidth}px - ${bp.maxWidth}px`;
  } else if (bp.minWidth !== null) {
    range = `> ${bp.minWidth - 1}px`;
  } else if (bp.maxWidth !== null) {
    range = `< ${bp.maxWidth + 1}px`;
  }

  return (
    <span className="text-[9px] text-[var(--color-muted-foreground)]">{range}</span>
  );
}

interface PropertyRowProps {
  readonly uid: string;
  readonly breakpoint: Breakpoint;
  readonly property: string;
  readonly value: string;
  readonly cascadedValue: string | undefined;
  readonly onChange: (value: string | undefined) => void;
  readonly onRemove: () => void;
}

function PropertyRow({ uid, breakpoint, property, value, cascadedValue, onChange, onRemove }: PropertyRowProps) {
  const inputId = `${uid}-${breakpoint}-${property}`;
  const isOverriding = value !== "" && cascadedValue !== undefined && value !== cascadedValue;

  return (
    <div className="flex items-center gap-1.5">
      <label
        htmlFor={inputId}
        className="w-20 shrink-0 truncate text-[10px] font-medium text-[var(--color-muted-foreground)]"
        title={property}
      >
        {property}
      </label>
      <input
        id={inputId}
        type="text"
        value={value}
        placeholder={cascadedValue ?? "value"}
        onChange={(e) => onChange(e.target.value || undefined)}
        className={[
          "h-6 flex-1 rounded border bg-[var(--color-card)] px-1.5 text-[10px] text-[var(--color-foreground)]",
          isOverriding ? "border-[var(--color-primary)]" : "border-[var(--color-border)]",
        ].join(" ")}
      />
      <button
        type="button"
        onClick={onRemove}
        className="text-[var(--color-muted-foreground)] hover:text-[var(--color-destructive)]"
        title="Remove override"
      >
        <span className="text-[10px]">x</span>
      </button>
    </div>
  );
}

interface AddPropertyButtonProps {
  readonly breakpoint: Breakpoint;
  readonly existingProperties: readonly string[];
  readonly onAdd: (property: string) => void;
}

function AddPropertyButton({ breakpoint, existingProperties, onAdd }: AddPropertyButtonProps) {
  const available = COMMON_PROPERTIES.filter((p) => !existingProperties.includes(p));

  if (available.length === 0) return null;

  return (
    <select
      value=""
      onChange={(e) => {
        if (e.target.value) onAdd(e.target.value);
      }}
      className="mt-1 h-6 rounded border border-dashed border-[var(--color-border)] bg-transparent px-1.5 text-[10px] text-[var(--color-muted-foreground)]"
    >
      <option value="">+ Add property</option>
      {available.map((prop) => (
        <option key={prop} value={prop}>
          {prop}
        </option>
      ))}
    </select>
  );
}

// ── Helpers ──

/**
 * Get the cascaded value for a property at a given breakpoint.
 * Desktop values cascade to tablet, tablet to mobile.
 */
function getCascadedValue(
  config: ResponsiveConfig,
  breakpoint: Breakpoint,
  property: string,
): string | undefined {
  if (breakpoint === "tablet") {
    return config.desktop[property];
  }
  if (breakpoint === "mobile") {
    return config.tablet[property] ?? config.desktop[property];
  }
  return undefined;
}

/**
 * Detect current viewport breakpoint.
 */
function useActiveBreakpoint(): Breakpoint {
  if (typeof window === "undefined") return "desktop";
  const width = window.innerWidth;
  if (width <= 767) return "mobile";
  if (width <= 1024) return "tablet";
  return "desktop";
}

/**
 * Generate CSS @media queries from responsive config.
 */
export function generateResponsiveCss(config: ResponsiveConfig, selector: string): string {
  const lines: string[] = [];

  // Desktop (base styles, no media query)
  const desktopProps = Object.entries(config.desktop).filter(([, v]) => v !== undefined && v !== "");
  if (desktopProps.length > 0) {
    lines.push(`${selector} {`);
    for (const [prop, val] of desktopProps) {
      lines.push(`  ${prop}: ${val};`);
    }
    lines.push("}");
  }

  // Tablet
  const tabletProps = Object.entries(config.tablet).filter(([, v]) => v !== undefined && v !== "");
  if (tabletProps.length > 0) {
    lines.push("");
    lines.push(`@media (min-width: ${BREAKPOINTS.tablet.minWidth}px) and (max-width: ${BREAKPOINTS.tablet.maxWidth}px) {`);
    lines.push(`  ${selector} {`);
    for (const [prop, val] of tabletProps) {
      lines.push(`    ${prop}: ${val};`);
    }
    lines.push("  }");
    lines.push("}");
  }

  // Mobile
  const mobileProps = Object.entries(config.mobile).filter(([, v]) => v !== undefined && v !== "");
  if (mobileProps.length > 0) {
    lines.push("");
    lines.push(`@media (max-width: ${BREAKPOINTS.mobile.maxWidth}px) {`);
    lines.push(`  ${selector} {`);
    for (const [prop, val] of mobileProps) {
      lines.push(`    ${prop}: ${val};`);
    }
    lines.push("  }");
    lines.push("}");
  }

  return lines.length > 0 ? lines.join("\n") : "/* No responsive overrides */";
}
